if (!customElements.get("same-artist-products")) {
  class SameArtistProducts extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector("[data-artist-view-all] button");
      if (button && !this.onLoadMore) {
        this.onLoadMore = () => this.loadProducts(true);
        button.addEventListener("click", this.onLoadMore);
      }
      const retry = this.querySelector("[data-artist-retry]");
      if (retry && !this.onRetry) {
        this.onRetry = () => this.loadProducts();
        retry.addEventListener("click", this.onRetry);
      }
      if (!this.loaded) this.loadProducts();
    }

    disconnectedCallback() {
      if (this.controller) this.loaded = false;
      this.controller?.abort();
      this.controller = null;
      this.setLoading(false);
    }

    showStatus(message, canRetry = false) {
      const status = this.querySelector("[data-artist-status]");
      if (!status) return;
      status.hidden = !message;
      if (message) status.querySelector("p").textContent = message;
      const retry = this.querySelector("[data-artist-retry]");
      if (retry) retry.hidden = !canRetry;
    }

    normalizeArtist(value = "") {
      return value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
    }

    matchesArtist(card) {
      const artistId = this.dataset.artistId;
      const candidateId = card.dataset.artistId;
      // Distinct references must never be merged just because names coincide.
      if (artistId && candidateId) return candidateId === artistId;
      const name = this.normalizeArtist(this.dataset.artistName);
      return Boolean(name && this.normalizeArtist(card.dataset.artistName) === name);
    }

    async fetchResults(url, controller) {
      // Retry a transient failure once, and put a time limit on each request.
      for (let attempt = 0; attempt < 2; attempt++) {
        if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
        const requestController = new AbortController();
        const abort = () => requestController.abort();
        controller.signal.addEventListener("abort", abort, { once: true });
        const timeout = setTimeout(abort, 10000);
        try {
          const response = await fetch(url.href, {
            credentials: "same-origin",
            signal: requestController.signal,
          });
          if (!response.ok) {
            const error = new Error("Artist products request failed (" + response.status + ").");
            error.retryable = response.status === 429 || response.status >= 500;
            throw error;
          }
          const html = new DOMParser().parseFromString(await response.text(), "text/html");
          const results = html.querySelector("[data-artist-product-results]");
          if (!results) throw new Error("Artist product results were not returned.");
          return results;
        } catch (error) {
          if (controller.signal.aborted || attempt === 1 || error.retryable === false) throw error;
        } finally {
          clearTimeout(timeout);
          controller.signal.removeEventListener("abort", abort);
        }
      }
    }

    hasMore() {
      // Only offer View All once another matching product has been confirmed.
      return this.state.rendered < this.state.cards.length;
    }

    setLoading(loading) {
      if (loading) this.setAttribute("aria-busy", "true");
      else this.removeAttribute("aria-busy");
      const retry = this.querySelector("[data-artist-retry]");
      if (retry) retry.disabled = loading;
      const button = this.querySelector("[data-artist-view-all]")?.querySelector("button");
      if (!button) return;
      button.disabled = loading;
      const label = button.querySelector("[data-artist-button-label]");
      if (!label) return;
      if (this.buttonLabel === undefined) this.buttonLabel = label.textContent;
      label.textContent = loading ? this.dataset.loadingLabel || "Loading..." : this.buttonLabel;
    }

    async loadProducts(showAll = false) {
      if (this.controller || (!this.dataset.artistId && !this.dataset.artistName) || !this.dataset.url) return;
      const content = this.querySelector("[data-artist-content]");
      const grid = this.querySelector("[data-artist-grid]");
      const viewAll = this.querySelector("[data-artist-view-all]");
      if (!content || !grid) return;

      if (!this.state) {
        this.state = {
          cards: [],
          productIds: new Set([this.dataset.productId]),
          rendered: 0,
          sources: [...new Set([this.dataset.url, this.dataset.fallbackUrl].filter(Boolean))].map((source) => ({
            url: new URL(source, window.location.origin),
            visited: new Set(),
          })),
        };
      }
      const controller = new AbortController();
      this.controller = controller;
      const state = this.state;
      const usedOnly = this.dataset.usedOnly === "true";
      this.expanded = this.expanded || showAll;
      const target = this.expanded ? Infinity : 4;
      // Look for a fifth match without showing it, so four-or-fewer never
      // displays View All. A click then exhausts all remaining search pages.
      const fetchTarget = this.expanded ? Infinity : viewAll ? 5 : 4;
      const failedSources = new Set();
      const isCurrent = () => !controller.signal.aborted && this.isConnected && this.controller === controller;
      this.setLoading(true);
      this.showStatus("");

      const renderCards = () => {
        if (!isCurrent()) return;
        const end = Math.min(target, state.cards.length);
        if (end <= state.rendered) return;
        grid.append(...state.cards.slice(state.rendered, end));
        state.rendered = end;
        content.hidden = false;
      };

      try {
        renderCards();
        while (state.cards.length < fetchTarget) {
          const source = state.sources.find((candidate) => candidate.url && !failedSources.has(candidate));
          if (!source) break;
          const url = new URL(source.url);
          url.searchParams.set("section_id", "same-artist-product-results");
          if (url.origin !== window.location.origin || source.visited.has(url.href)) {
            source.url = null;
            continue;
          }
          try {
            const results = await this.fetchResults(url, controller);
            if (!isCurrent()) return;
            source.visited.add(url.href);
            const activeArtists = (results.dataset.activeArtist || "")
              .split(",")
              .map((value) => value.trim().replace("gid://shopify/Metaobject/", ""));
            const missingArtistFilter = url.searchParams.has("filter.p.m.evp.artist") && !activeArtists.includes(this.dataset.artistId);

            // Keep the whole page for the eventual View All click.
            for (const card of results.querySelectorAll("[data-artist-product]")) {
              if (!this.matchesArtist(card) || !card.dataset.productId || state.productIds.has(card.dataset.productId)) continue;
              if (card.dataset.inStock !== "true" || card.dataset.isUsed !== (usedOnly ? "true" : "false")) continue;
              state.productIds.add(card.dataset.productId);
              state.cards.push(card);
            }
            source.url = null;
            // Keep individually verified matches even if Shopify omitted the
            // filter, then switch to the artist-name search for further results.
            if (!missingArtistFilter && results.dataset.nextUrl) {
              const page = new URL(results.dataset.nextUrl, url).searchParams.get("page");
              if (page && /^\d+$/.test(page)) {
                // Retain the original artist query, filters, and localized route.
                url.searchParams.set("page", page);
                if (!source.visited.has(url.href)) source.url = url;
              }
            }
            renderCards();
          } catch (error) {
            if (!isCurrent()) return;
            // Leave the failed cursor intact so the button can retry this page.
            failedSources.add(source);
            console.warn("Unable to load products from the same artist:", error);
          }
        }

        if (!isCurrent()) return;
        this.loaded = state.rendered > 0 || !failedSources.size;
        if (failedSources.size) {
          this.showStatus("More products could not be loaded. Please try again.", true);
        } else if (!state.rendered && this.dataset.designMode === "true") {
          this.showStatus("No other in-stock products matching this artist and condition were found. This section stays hidden until matching products are available.");
        }
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.setLoading(false);
          if (viewAll) viewAll.hidden = !this.hasMore();
        }
      }
    }
  }

  customElements.define("same-artist-products", SameArtistProducts);
}
